import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CalendarRange, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { useWorkingContext } from '@/hooks/useWorkingContext';
import { useCampusPicker } from '@/hooks/usePickers';
import { gradeOfferingsApi } from '@/services/api/endpoints/academic';
import { examinationsApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { Examination, ExaminationStatus } from '@/types/examination';
import { formatDate, orDash } from '@/utils/format';
import { EXAMINATION_STATUSES, useExaminationTypes, useGradingScales, useTerms } from './shared';

const schema = z
  .object({
    gradeOfferingId: z.string().uuid('Select a class'),
    termId: z.string(),
    examinationTypeId: z.string().uuid('Select a type'),
    code: z.string().min(1, 'Code is required').max(32),
    name: z.string().min(1, 'Name is required').max(150),
    description: z.string().max(500).optional(),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    gradingScaleId: z.string(),
  })
  .refine((values) => values.endDate >= values.startDate, {
    path: ['endDate'],
    message: 'The last day cannot fall before the first',
  });

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  gradeOfferingId: '',
  termId: '',
  examinationTypeId: '',
  code: '',
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  gradingScaleId: '',
};

/**
 * Examinations for the working year.
 *
 * The counters above the table are read from `totalElements` on four filtered
 * queries rather than counted from the visible page — a page of twenty rows
 * cannot tell you how many drafts the school has, and a number that quietly
 * means "of the twenty you can see" is worse than no number.
 */
export function ExaminationsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const table = useTableQuery('startDate,desc');
  const context = useWorkingContext();

  const [termFilter, setTermFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<ExaminationStatus | ''>('');
  const [editing, setEditing] = useState<Examination | null>(null);
  const [open, setOpen] = useState(false);

  const terms = useTerms();
  const types = useExaminationTypes();
  const scales = useGradingScales();
  const campuses = useCampusPicker();

  const params = {
    ...table.params,
    ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
    ...(termFilter !== '' ? { termId: termFilter } : {}),
    ...(statusFilter !== '' ? { status: statusFilter } : {}),
  };

  const query = useQuery({
    queryKey: queryKeys.examinations.list(params),
    queryFn: () => examinationsApi.list(params),
  });

  // One cheap request per counter: size 1, read the total, ignore the row.
  const COUNTERS: { status: ExaminationStatus; label: string }[] = [
    { status: 'DRAFT', label: 'In preparation' },
    { status: 'SCHEDULED', label: 'Scheduled' },
    { status: 'ONGOING', label: 'Being sat' },
    { status: 'PUBLISHED', label: 'Published' },
  ];

  const counters = useQueries({
    queries: COUNTERS.map((counter) => {
      const counterParams = {
        page: 0,
        size: 1,
        status: counter.status,
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
      };
      return {
        queryKey: queryKeys.examinations.list(counterParams),
        queryFn: () => examinationsApi.list(counterParams),
      };
    }),
  });

  const [formCampus, setFormCampus] = useState('');

  const offerings = useQuery({
    queryKey: queryKeys.gradeOfferings.list({
      picker: true,
      year: context.academicYearId,
      campus: formCampus,
    }),
    queryFn: () =>
      gradeOfferingsApi.list({
        page: 0,
        size: 200,
        status: 'ACTIVE',
        ...(context.academicYearId !== null ? { academicYearId: context.academicYearId } : {}),
        ...(formCampus !== '' ? { campusId: formCampus } : {}),
      }),
    enabled: open && context.academicYearId !== null,
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  // A class chosen under the previous campus is no longer in the list.
  useEffect(() => {
    if (editing === null && open) {
      form.setValue('gradeOfferingId', '');
    }
  }, [formCampus, editing, open, form]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        gradeOfferingId: values.gradeOfferingId,
        examinationTypeId: values.examinationTypeId,
        code: values.code,
        name: values.name,
        startDate: values.startDate,
        endDate: values.endDate,
        ...(values.termId !== '' ? { termId: values.termId } : {}),
        ...(values.gradingScaleId !== '' ? { gradingScaleId: values.gradingScaleId } : {}),
        ...(values.description !== undefined && values.description !== ''
          ? { description: values.description }
          : {}),
      };
      return editing === null
        ? examinationsApi.create(payload)
        : examinationsApi.update(editing.id, payload);
    },
    onSuccess: (examination) => {
      toast.success(editing === null ? 'Examination created' : 'Examination updated');
      const wasCreate = editing === null;
      close();
      void query.refetch();
      // A new examination has no papers yet, so send the user straight to the
      // screen where they add them rather than back to a list row that is not
      // yet usable.
      if (wasCreate) {
        navigate(ROUTES.examinationDetail(examination.id));
      }
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  function openCreate() {
    setEditing(null);
    setFormCampus(context.campusId ?? '');
    form.reset({ ...EMPTY, termId: termFilter });
    setOpen(true);
  }

  function openEdit(examination: Examination) {
    setEditing(examination);
    setFormCampus('');
    form.reset({
      gradeOfferingId: examination.gradeOfferingId,
      termId: examination.termId ?? '',
      examinationTypeId: examination.examinationTypeId,
      code: examination.code,
      name: examination.name,
      description: examination.description ?? '',
      startDate: examination.startDate,
      endDate: examination.endDate,
      gradingScaleId: examination.gradingScaleId ?? '',
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  const columns: Column<Examination>[] = [
    {
      key: 'name',
      header: 'Examination',
      sortKey: 'name',
      render: (examination) => (
        <div>
          <span className="font-medium text-ink">{examination.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{examination.code}</span>
        </div>
      ),
    },
    {
      key: 'class',
      header: 'Class',
      render: (examination) => (
        <div>
          <span className="text-ink">{examination.gradeName}</span>
          <span className="block text-[11px] text-muted">{orDash(examination.campusName)}</span>
        </div>
      ),
    },
    { key: 'type', header: 'Type', render: (examination) => examination.examinationTypeName },
    { key: 'term', header: 'Term', render: (examination) => orDash(examination.termName) },
    {
      key: 'dates',
      header: 'Dates',
      sortKey: 'startDate',
      render: (examination) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatDate(examination.startDate)} – {formatDate(examination.endDate)}
        </span>
      ),
    },
    {
      key: 'papers',
      header: 'Papers',
      render: (examination) => (
        <span className="tabular-nums text-muted">
          {examination.subjectCount}
          {examination.scheduledCount > 0 && (
            <span className="text-subtle"> · {examination.scheduledCount} sittings</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (examination) => <StatusBadge status={examination.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (examination) => (
        <div className="flex justify-end gap-1">
          {can(PERMISSIONS.EXAMINATION_UPDATE) && examination.configurable && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(event) => {
                event.stopPropagation();
                openEdit(examination);
              }}
            >
              Edit
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={(event) => {
              event.stopPropagation();
              navigate(ROUTES.examinationDetail(examination.id));
            }}
          >
            Open
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {COUNTERS.map((counter, index) => {
          const result = counters[index];
          return (
            <Card key={counter.status} className="px-4 py-3.5">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-subtle">
                {counter.label}
              </span>
              <span className="mt-1 block text-[26px] leading-none tabular-nums text-ink">
                {result?.isPending === true ? '—' : (result?.data?.pagination.totalElements ?? 0)}
              </span>
            </Card>
          );
        })}
      </div>

      <ListPage
        title="Examinations"
        description="Every examination in the working academic year, from preparation through to published results."
        icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.EXAMINATION_CREATE) ? (
            <Button onClick={openCreate}>New examination</Button>
          ) : undefined
        }
        search={{
          value: table.searchInput,
          onChange: table.setSearchInput,
          placeholder: 'Search by name or code…',
        }}
        filters={
          <>
            <FilterSelect
              label="Term"
              value={termFilter}
              onChange={(value) => {
                setTermFilter(value);
                table.setPage(0);
              }}
              options={(terms.data ?? []).map((term) => ({ value: term.id, label: term.name }))}
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                table.setPage(0);
              }}
              options={EXAMINATION_STATUSES.map((status) => ({
                value: status.value,
                label: status.label,
              }))}
            />
          </>
        }
        pagination={query.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={query.data?.data}
          rowKey={(examination) => examination.id}
          loading={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(examination) => navigate(ROUTES.examinationDetail(examination.id))}
          emptyTitle="No examinations yet"
          emptyDescription="Create one to add papers, build a timetable and open marks entry."
          emptyIcon={<CalendarRange className="h-6 w-6" aria-hidden="true" />}
          emptyAction={
            can(PERMISSIONS.EXAMINATION_CREATE) ? (
              <Button onClick={openCreate}>New examination</Button>
            ) : undefined
          }
        />
      </ListPage>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'New examination' : `Edit ${editing.name}`}
        description="Papers, marks and the timetable are added once it exists."
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={form.handleSubmit((values) => save.mutate(values))}
              loading={save.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={form.handleSubmit((values) => save.mutate(values))}
        >
          {editing === null ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Campus"
                value={formCampus}
                onChange={(event) => setFormCampus(event.target.value)}
                hint="Narrows the class list."
              >
                <option value="">All campuses</option>
                {(campuses.data ?? []).map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </SelectField>

              <SelectField
                label="Class"
                required
                error={form.formState.errors.gradeOfferingId?.message}
                {...form.register('gradeOfferingId')}
              >
                <option value="">Select a class…</option>
                {(offerings.data?.data ?? []).map((offering) => (
                  <option key={offering.id} value={offering.id}>
                    {offering.grade.name} · {offering.campusName ?? "All campuses"}
                  </option>
                ))}
              </SelectField>
            </div>
          ) : (
            /*
             * The class is fixed after creation: it decides which children sat
             * the papers, so moving it would change the meaning of every mark
             * already recorded against them.
             */
            <div className="border border-line bg-surface-2 px-3.5 py-3">
              <span className="block text-[11px] font-semibold uppercase tracking-wider text-subtle">
                Class
              </span>
              <span className="mt-0.5 block text-sm text-ink">
                {editing.gradeName} · {orDash(editing.campusName)}
              </span>
              <p className="mt-1.5 text-xs text-muted">
                Fixed after creation — it decides whose marks these are.
              </p>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Examination type"
              required
              error={form.formState.errors.examinationTypeId?.message}
              {...form.register('examinationTypeId')}
            >
              <option value="">Select a type…</option>
              {(types.data ?? [])
                .filter((type) => type.status === 'ACTIVE')
                .map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
            </SelectField>

            <SelectField
              label="Term"
              hint="Optional — an entrance test sits outside the term structure."
              error={form.formState.errors.termId?.message}
              {...form.register('termId')}
            >
              <option value="">No term</option>
              {(terms.data ?? []).map((term) => (
                <option key={term.id} value={term.id}>
                  {term.name}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              placeholder="MID-G5"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField
              label="Name"
              required
              placeholder="Mid-Term Term 1 · Grade 5"
              error={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="First day"
              type="date"
              required
              error={form.formState.errors.startDate?.message}
              {...form.register('startDate')}
            />
            <TextField
              label="Last day"
              type="date"
              required
              error={form.formState.errors.endDate?.message}
              {...form.register('endDate')}
            />
          </div>

          <SelectField
            label="Grading scale"
            hint="Leave blank to use the school's default scale."
            error={form.formState.errors.gradingScaleId?.message}
            {...form.register('gradingScaleId')}
          >
            <option value="">School default</option>
            {(scales.data ?? []).map((scale) => (
              <option key={scale.id} value={scale.id}>
                {scale.name}
                {scale.defaultScale ? ' (default)' : ''}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Description"
            placeholder="Anything staff need to know about this examination"
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />
        </form>
      </Modal>
    </>
  );
}
